import React, { Component } from 'react'
import { connect } from 'react-redux'
import cn from 'classnames'

import { Trans } from '@lingui/react'

import SerialEpisode from './serial-episode'
import { Dropdown } from 'client/shared/blocks/dropdown'

import bookHelper from 'shared/tools/book-helper'
import { hasAppropriateActiveSubscription } from 'client/shared/helpers/access-helpers'

import { getNonExpiredAccessLevels } from 'client/bookmate/selectors/subscription-selectors'

import InfiniteScroll from 'client/shared/blocks/infinite-scroll'

import {
  getBookById,
  getBooksByIds,
} from 'client/bookmate/selectors/book-selector'
import {
  SERIAL,
  loadEpisodes,
  clearSerialEpisodes,
} from 'client/bookmate/reducers/serial-reducer'

import { SerialProps, SerialEpisodes } from 'client/shared/types/serial'

import './index.styl'
import { Dispatch, State as ConnectedState } from 'shared/types/redux'
import { sendOnClickAnalytics } from 'shared/tools/analytics-helper'
import { QueryParams } from 'shared/tools/url-helper'

const DROPDOWN_TOP_OFFSET = 20
const EPISODES_PER_PAGE = 20

type Props = {
  serial: SerialProps
  episodes: SerialEpisodes
  locale: string
  accessLevels: string[]
  dispatch: Dispatch
  userId: number
  query?: QueryParams
}

type State = {
  filter: string
  dropdownVisibility: boolean
  order: Order
}

type Order = 'desc' | 'asc'

class SerialEpisodesList extends Component<Props, State> {
  state = {
    filter: 'all',
    dropdownVisibility: false,
    order:
      this.props.serial.library_card &&
      this.props.serial.library_card.progress > 0
        ? ('desc' as Order)
        : ('asc' as Order),
  }

  load = (append = true): void => {
    const { dispatch, episodes, serial } = this.props
    const { filter } = this.state

    return dispatch(
      loadEpisodes({
        uuid: serial.uuid,
        page: !append ? 1 : episodes[`${filter}EpisodesPage`],
        per_page: EPISODES_PER_PAGE,
        append,
        order: this.state.order,
        filter,
      }),
    )
  }

  setFilter = (value: string) => {
    const { episodes } = this.props
    this.setState({ filter: value }, () => {
      if (!episodes[value].length) this.load()
    })
  }

  setVisibility = (value: boolean): void => {
    this.setState({ dropdownVisibility: value })
  }

  setOrder = (value: string): void => {
    const { dispatch, serial, episodes } = this.props
    const filter = `${this.state.filter}EpisodesPage`
    if (
      serial.episodes_count / EPISODES_PER_PAGE <= episodes[filter] ||
      !episodes[filter]
    ) {
      this.setState({ order: value as Order })
    } else {
      dispatch(clearSerialEpisodes())
      this.setState({ order: value as Order }, () => this.load(false))
    }
  }

  prepareEpisodes = () => {
    const { order } = this.state
    const { episodes } = this.props

    const sortFunc = {
      asc: (a: { position: number }, b: { position: number }) =>
        a.position - b.position,
      desc: (a: { position: number }, b: { position: number }) =>
        b.position - a.position,
    }

    return episodes[this.state.filter].sort(sortFunc[order])
  }

  render(): JSX.Element {
    const {
      serial: { can_be_read, in_library, uuid },
      locale,
      accessLevels,
      episodes,
      userId,
      dispatch,
      query,
    } = this.props
    const { filter } = this.state

    return (
      <div className="serial-episodes-page">
        <div className="serial-episodes-list">
          {in_library && (
            <div className="serial-episodes-list__header">
              <div className="serial-episodes-list__filters">
                {this.renderFilter()}
              </div>
              <div
                className="serial-episodes-list__sort-button"
                onClick={() =>
                  this.setVisibility(!this.state.dropdownVisibility)
                }
              >
                {this.renderDropdown()}
              </div>
            </div>
          )}
          <div className="serial-episodes-list__items">
            <InfiniteScroll
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              load={this.load}
              page={episodes[`${filter}EpisodesPage`]}
            >
              {episodes[filter] &&
                this.prepareEpisodes().map((episode, index) => (
                  <SerialEpisode
                    query={query}
                    id={`${episode.uuid} ${index + 1}`}
                    onClick={() =>
                      sendOnClickAnalytics({
                        dispatch,
                        resourceType: SERIAL,
                        itemUuid: episode.uuid,
                        screenName: `${SERIAL}_episodes_page`,
                        position: index + 1,
                        userId,
                        resourceUuid: uuid,
                      })
                    }
                    key={episode.uuid}
                    episode={episode}
                    canSerialBeRead={can_be_read}
                    locale={locale}
                    hasAccess={hasAppropriateActiveSubscription(
                      episode,
                      accessLevels,
                    )}
                  />
                ))}
            </InfiniteScroll>
          </div>
        </div>
      </div>
    )
  }

  renderDropdown = (): JSX.Element => {
    const { order, dropdownVisibility } = this.state

    return (
      <Dropdown
        position="top-center"
        offsetTop={DROPDOWN_TOP_OFFSET}
        onClose={() => this.setVisibility(false)}
        hidden={!dropdownVisibility}
      >
        <div className="serial-episodes-list__dropdown-content">
          <div
            className={cn('episodes-sort-option', {
              'episodes-sort-option_active': order === 'asc',
            })}
            onClick={() => this.setOrder('asc')}
          >
            <Trans id="book.serial_episodes_sort_begin" />
          </div>
          <div
            className={cn('episodes-sort-option', {
              'episodes-sort-option_active': order === 'desc',
            })}
            onClick={() => this.setOrder('desc')}
          >
            <Trans id="book.serial_episodes_sort_date" />
          </div>
        </div>
      </Dropdown>
    )
  }

  renderFilter = (): JSX.Element => {
    const { filter } = this.state

    const {
      serial: { library_card },
    } = this.props

    return (
      <>
        <span
          className={cn({
            'serial-episodes-list__filters_inactive': filter !== 'all',
            'serial-episodes-list__filters_active': filter === 'all',
          })}
          onClick={() => this.setFilter('all')}
        >
          <Trans id="book.serial_filters_all" />
        </span>
        {!bookHelper.isFinished(library_card) && (
          <span
            className={cn({
              'serial-episodes-list__filters_inactive': filter !== 'unfinished',
              'serial-episodes-list__filters_active': filter === 'unfinished',
            })}
            onClick={() => this.setFilter('unfinished')}
          >
            <Trans id="book.serial_filters_unfinished" />
          </span>
        )}
      </>
    )
  }
}

const connectWrapper = connect((state: ConnectedState) => ({
  serial: getBookById(state, state.serial.uuid),
  episodes: {
    all: getBooksByIds(state, state.serial.episodes.all),
    unfinished: getBooksByIds(state, state.serial.episodes.unfinished),
    allEpisodesPage: state.serial.episodes.allEpisodesPage,
    unfinishedEpisodesPage: state.serial.episodes.unfinishedEpisodesPage,
  },
  locale: state.currentUser.data.locale,
  accessLevels: getNonExpiredAccessLevels(state),
  userId: state.currentUser.data.id,
  query: state.app.storedQuery,
}))

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connectWrapper(SerialEpisodesList)
